import { useState, useCallback, useEffect } from 'react';
import { suppliersApi } from '../configs/api_endpoints.config';
import { apiService } from '../services/api.service';
import useAuthProviderStore from '../stores/auth_provider.store';
import permissionManager from '../lib/permission.manager';

export function useSupplierSearch() {
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierResults, setSupplierResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [autoLoadedSupplier, setAutoLoadedSupplier] = useState(null);

  const { userPermissions } = useAuthProviderStore();

  useEffect(() => {
    const fetchExternalUserSupplier = async () => {
      if (!userPermissions) return;

      const isExternal = permissionManager.isUserSupplier(userPermissions);
      if (!isExternal) return;

      setIsSearching(true);
      setSearchError('');

      try {
        const response = await apiService.get(suppliersApi.search, {
          offset: 0,
          limit: 1,
          includes: 'factories',
        });

        const suppliersArray = response.data?.data || response.data;

        if (
          suppliersArray &&
          Array.isArray(suppliersArray) &&
          suppliersArray.length > 0
        ) {
          const supplier = suppliersArray[0];
          setAutoLoadedSupplier(supplier);
          setSupplierResults([supplier]);
        }
      } catch (error) {
        console.error('Error fetching supplier for external user:', error);
        setSearchError('Failed to load supplier data');
      } finally {
        setIsSearching(false);
      }
    };

    fetchExternalUserSupplier();
  }, [userPermissions]);

  const searchSuppliers = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setSupplierResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setSearchError('');

    try {
      const response = await apiService.get(suppliersApi.search, {
        offset: 0,
        limit: 10,
        search: searchTerm,
        includes: 'factories',
      });

      const suppliersArray = response.data?.data || response.data;

      if (suppliersArray && Array.isArray(suppliersArray)) {
        setSupplierResults(suppliersArray);
        setShowResults(true);
      } else {
        setSupplierResults([]);
        setShowResults(false);
      }
    } catch (error) {
      console.error('Error searching suppliers:', error);
      setSearchError('Failed to search suppliers');
      setSupplierResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  return {
    supplierSearch,
    setSupplierSearch,
    supplierResults,
    isSearching,
    searchError,
    showResults,
    setShowResults,
    searchSuppliers,
    autoLoadedSupplier,
  };
}
